<template>
  <div
    class="proposal-card"
    :class="{
      'proposal-card--committed': committed,
      'proposal-card--dismissed': dismissed,
      'proposal-card--dismissing': dismissing && !dismissed
    }"
  >
    <header class="proposal-card__head">
      <div class="proposal-card__tag">
        <q-icon name="bolt" size="13px" />
        <span>{{ actionLabel }}</span>
      </div>
      <span
        v-if="showConfidence && !dismissed"
        class="proposal-card__confidence"
        :class="confidenceClass"
        title="Assistant's confidence that this rule does what you asked for"
      >
        {{ confidencePct }}%
      </span>
    </header>

    <p class="proposal-card__summary" v-html="renderInlineMarkdown(proposal.summary)" />

    <dl class="proposal-card__meta">
      <div v-if="scopeLabel">
        <dt>Scope</dt>
        <dd>{{ scopeLabel }}</dd>
      </div>
      <div v-if="proposal.fieldAffected">
        <dt>Field</dt>
        <dd><code>{{ proposal.fieldAffected }}</code></dd>
      </div>
      <div v-if="proposal.appliesWhen" class="proposal-card__meta-wide">
        <dt>Applies when</dt>
        <dd v-html="renderInlineMarkdown(proposal.appliesWhen)" />
      </div>
      <div v-if="proposal.doesNotApplyWhen" class="proposal-card__meta-wide">
        <dt>Does not apply when</dt>
        <dd v-html="renderInlineMarkdown(proposal.doesNotApplyWhen)" />
      </div>
    </dl>

    <ul v-if="proposal.conflicts && proposal.conflicts.length" class="proposal-card__conflicts">
      <li v-for="conflict in proposal.conflicts" :key="conflict.ruleId">
        <q-icon name="warning_amber" size="12px" />
        <div class="proposal-card__conflict-body">
          <div
            class="proposal-card__conflict-summary"
            v-html="renderInlineMarkdown(conflict.summary)"
          />
          <div
            class="proposal-card__conflict-reason"
            v-html="renderInlineMarkdown(conflict.reason)"
          />
        </div>
      </li>
    </ul>

    <footer class="proposal-card__footer">
      <span v-if="committed" class="proposal-card__status proposal-card__status--ok">
        <q-icon name="check_circle" size="14px" />
        Applied{{ committedRuleId ? ` as ${committedRuleId}` : '' }}
      </span>
      <span v-else-if="dismissed" class="proposal-card__status proposal-card__status--muted">
        Dismissed
      </span>
      <span v-else-if="error" class="proposal-card__status proposal-card__status--err">
        <q-icon name="error_outline" size="14px" />
        {{ error }}
      </span>
      <span v-else-if="proposal.requiresElevatedPermission" class="proposal-card__status proposal-card__status--warn">
        <q-icon name="lock" size="13px" />
        Needs elevated permission
      </span>
      <span v-else class="proposal-card__status proposal-card__status--muted">Ready to apply</span>

      <div v-if="!committed && !dismissed" class="proposal-card__actions">
        <button
          type="button"
          class="proposal-card__dismiss"
          :disabled="busy || previewing || dismissing"
          aria-label="Dismiss proposal"
          title="Dismiss this suggestion"
          @click="$emit('dismiss', proposal.proposalId)"
        >
          <template v-if="dismissing">
            <q-spinner size="12px" />
            <span>Dismissing…</span>
          </template>
          <template v-else>
            <q-icon name="close" size="14px" />
            <span>Dismiss</span>
          </template>
        </button>

        <button
          v-if="!bundleControlsActive"
          type="button"
          class="proposal-card__test"
          :class="{ 'proposal-card__test--running': previewing }"
          :disabled="busy || previewing || dismissing"
          @click="$emit('preview', proposal.proposalId)"
        >
          <template v-if="previewing">
            <q-spinner size="12px" />
            <span>Previewing…</span>
          </template>
          <template v-else>
            <q-icon name="play_arrow" size="14px" />
            <span>Preview</span>
          </template>
        </button>

        <button
          v-if="!bundleControlsActive"
          type="button"
          class="proposal-card__confirm"
          :disabled="busy || previewing || dismissing || proposal.requiresElevatedPermission"
          @click="$emit('confirm', proposal.proposalId)"
        >
          <template v-if="busy">
            <q-spinner size="12px" />
            <span>Applying…</span>
          </template>
          <span v-else>Apply</span>
        </button>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { renderInlineMarkdown } from '../formatting';
import type { ProposalCard } from '../types';

const props = defineProps<{
  proposal: ProposalCard;
  busy?: boolean;
  previewing?: boolean;
  committed?: boolean;
  committedRuleId?: string | null;
  dismissed?: boolean;
  dismissing?: boolean;
  error?: string | null;
  // Set when the card is part of a bundle that still has 2+ pending siblings, so per-card
  // Preview and Apply would have misleading semantics (Preview runs only this rule against
  // the baseline document, ignoring sibling rules the user has not yet decided on; Apply
  // lets the user commit half a bundle without seeing the combined effect). Bundle-level
  // "Preview all" and "Apply all" remain the only paths in that case.
  bundleControlsActive?: boolean;
}>();

defineEmits<{
  (e: 'confirm', proposalId: string): void;
  (e: 'preview', proposalId: string): void;
  (e: 'dismiss', proposalId: string): void;
}>();

const actionLabel = computed(() => {
  switch (props.proposal.suggestedAction) {
    case 'CreateRule': return 'New rule';
    case 'UpdateRule': return 'Update rule';
    case 'DeactivateRule': return 'Deactivate rule';
    default: return props.proposal.suggestedAction;
  }
});

const scopeLabel = computed(() => {
  const { scope, scopeTargetName } = props.proposal;
  if (!scope) return '';
  return scopeTargetName ? `${scope} · ${scopeTargetName}` : scope;
});

const confidencePct = computed(() => Math.round((props.proposal.confidence ?? 0) * 100));

// Upstream sometimes leaves confidence at 0 (meaning "not provided") rather than
// emitting a real value. Rendering a red 0% on every proposal is alarming and
// uninformative — hide the pill until the agent gives us a meaningful number.
const showConfidence = computed(() => {
  const raw = props.proposal.confidence;
  return raw !== null && raw !== undefined && raw > 0;
});

const confidenceClass = computed(() => {
  const pct = confidencePct.value;
  if (pct >= 80) return 'proposal-card__confidence--high';
  if (pct >= 50) return 'proposal-card__confidence--mid';
  return 'proposal-card__confidence--low';
});
</script>

<style scoped lang="scss">
.proposal-card {
  margin: 6px 0;
  padding: 12px 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #d8e3ff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  // Adapt the footer to the card's own width rather than the chat panel's. The
  // chat bubble caps at 560 px while the outer `ra` container can be much
  // wider, so a query on `ra` only fires when the panel is narrow — not when
  // the card itself is. Transient labels ("Previewing…", "Dismissing…") push
  // the action row past the card edge in those cases.
  container-type: inline-size;
  container-name: proposal-card;

  &--committed {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }

  &--dismissed {
    background: #f8fafc;
    border-color: #e5e7eb;
    color: #6b7280;
    box-shadow: none;

    .proposal-card__tag {
      background: #f1f5f9;
      color: #6b7280;
    }

    .proposal-card__summary,
    .proposal-card__meta dd,
    code {
      color: #6b7280;
    }
  }

  &--dismissing {
    opacity: 0.65;
    transition: opacity 120ms ease;
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 999px;
    background: #eef2ff;
    color: #1d4ed8;
    font-size: 11.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__confidence {
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
    font-variant-numeric: tabular-nums;

    &--high { color: #047857; }
    &--mid { color: #b45309; }
    &--low { color: #b91c1c; }
  }

  &__summary {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.5;
    color: #111827;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
    font-size: 0.92em;
    padding: 1px 5px;
    background: #f1f5f9;
    color: #1e293b;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    word-break: break-word;
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 12px;
    margin: 10px 0 0;
    padding: 0;
    font-size: 12px;

    > div { display: flex; flex-direction: column; gap: 1px; }

    dt {
      color: #6b7280;
      font-weight: 500;
      letter-spacing: 0.01em;
    }

    dd {
      margin: 0;
      color: #111827;
      word-break: break-word;
    }
  }

  &__meta-wide { grid-column: span 2; }

  &__conflicts {
    list-style: none;
    margin: 10px 0 0;
    padding: 8px 10px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 8px;
    font-size: 12px;
    color: #92400e;

    li { display: flex; gap: 6px; align-items: flex-start; line-height: 1.4; }
    li + li { margin-top: 4px; }
  }

  &__conflict-body { display: flex; flex-direction: column; gap: 2px; }
  &__conflict-summary { font-weight: 600; }
  &__conflict-reason { color: #b45309; }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    white-space: nowrap;

    &--ok { color: #047857; }
    &--err { color: #b91c1c; }
    &--warn { color: #b45309; }
    &--muted { color: #6b7280; }
  }

  &__actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__test {
    min-width: 70px;
    height: 30px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid #c7d2fe;
    background: #ffffff;
    color: #1e40af;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;
    transition: background-color 120ms ease, border-color 120ms ease;

    &:hover:not(:disabled) { background: #eef2ff; border-color: #93b4f5; }
    &:disabled { color: #9ca3af; border-color: #e5e7eb; cursor: not-allowed; }

    &--running:disabled {
      background: #eef2ff;
      border-color: #c7d2fe;
      color: #1e40af;
    }
  }

  &__confirm {
    min-width: 78px;
    height: 30px;
    padding: 0 14px;
    border-radius: 8px;
    border: none;
    background: #3b6cf3;
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;
    transition: background-color 120ms ease, opacity 120ms ease;

    &:hover:not(:disabled) { background: #2952d8; }
    &:disabled { background: #d8dee9; cursor: not-allowed; }
  }

  &__dismiss {
    height: 30px;
    padding: 0 10px;
    border-radius: 8px;
    border: 1px solid transparent;
    background: transparent;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    white-space: nowrap;
    transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;

    &:hover:not(:disabled) {
      background: #f3f4f6;
      color: #374151;
      border-color: #e5e7eb;
    }
    &:disabled { color: #9ca3af; cursor: not-allowed; }
  }
}

// Below ~480 px of card width the footer's status pill + 3 buttons no longer
// fit on one row (with localized or transient "Previewing…"/"Dismissing…"
// labels the row can be even wider). Stack the footer and let the actions
// span the full width as equal-flex buttons.
@container proposal-card (max-width: 479px) {
  .proposal-card { padding: 10px 12px; }
  .proposal-card__footer {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin-top: 10px;
  }
  .proposal-card__actions {
    width: 100%;
    display: flex;
    gap: 6px;
  }
  .proposal-card__test,
  .proposal-card__confirm {
    flex: 1 1 0;
    min-width: 0;
    padding: 0 8px;
  }
  .proposal-card__dismiss {
    flex: 0 1 auto;
    padding: 0 8px;
  }
  .proposal-card__status { order: -1; }
}

@container proposal-card (max-width: 339px) {
  .proposal-card { padding: 9px 10px; }
  .proposal-card__head { margin-bottom: 6px; }
  .proposal-card__summary { font-size: 13px; }
  .proposal-card__meta {
    grid-template-columns: 1fr;
    gap: 4px 0;
    font-size: 11.5px;
  }
  .proposal-card__meta-wide { grid-column: span 1; }
  .proposal-card__actions { flex-wrap: wrap; }
  .proposal-card__dismiss {
    width: 100%;
    flex-basis: 100%;
    order: 2;
    height: 28px;
  }
  .proposal-card__test,
  .proposal-card__confirm {
    height: 30px;
    font-size: 12.5px;
  }
  .proposal-card__tag { font-size: 11px; padding: 2px 6px; }
}
</style>
